import { settings } from "app";
import permissions from "app/permissions";
import CustomFilter from "features/CustomFilter";
import { MobileListRow } from "features/MobileListRow";
import React, { useEffect, useState } from "react";
import { Datagrid, FunctionField, List, SimpleList, TextField, TopToolbar, useStore } from "react-admin";
import { useMediaQuery } from "@mui/material";
import dataProvider from "server/dataProvider";
import SecuredComponent from "server/security/SecuredComponent";
import ImportResultsDialog from "./components/ImportResultsDialog";
import { ActivityServicePath, ASSIGNMENTS, FOREIGN_LANGUAGES, LEVELS, MOBILE_BREAKPOINT } from "utils/constants";
import DownloadResultsTemplate from "./components/DownloadResultsTemplate";
import ManageStudentsLevelDialog from "./components/ManageStudentsDialog";
import { activityRequests } from "api/activityApi";
import { useNotifyError } from "utils/useNotifyError";

const ListActions = ({ foreignLanguageId }) => {
  return (
    <TopToolbar>
      <SecuredComponent permission={permissions.canManageForeignLanguagesEntryTests}>
        <DownloadResultsTemplate />
      </SecuredComponent>
      <SecuredComponent permission={permissions.canManageForeignLanguagesEntryTests}>
        <ImportResultsDialog foreignLanguageId={foreignLanguageId} />
      </SecuredComponent>
    </TopToolbar>
  );
};

const ListForeignLanguagesStudents = () => {
  const notifyError = useNotifyError();
  const isMobile = useMediaQuery(MOBILE_BREAKPOINT);
  const STORE_KEY = "foreignLanguageEntryTests.filters";
  // filters drives the <List> and the gate (persisted via useStore);
  // foreignLanguageId is the draft bound to CustomFilter, committed on "Провери".
  const [filters, setFilters] = useStore(STORE_KEY, null);
  const [foreignLanguageId, setForeignLanguageId] = useState(filters?.foreignLanguageId ?? "");
  const [foreignLanguages, setForeignLanguages] = useState([]);

  useEffect(() => {
    const loadForeignLanguages = async () => {
      const hasEntryTestQuery = "?hasEntryTest=true";
      try {
        const { data } = await dataProvider.customRequest(
          activityRequests.getActivityForeignLanguagesSelect(),
          null,
          "GET",
          hasEntryTestQuery,
        );
        setForeignLanguages(data);
      } catch (err) {
        notifyError(err);
      }
    };
    loadForeignLanguages();
  }, [notifyError]);

  const handleSearch = () => {
    if (!foreignLanguageId) return;

    setFilters({ foreignLanguageId });
  };

  return (
    <>
      <CustomFilter
        title="Избери чужд език"
        buttonLabel="Провери"
        buttonDisabled={!foreignLanguageId}
        buttonOnClick={handleSearch}
        filters={[
          {
            type: "select",
            label: "Чужд език",
            value: foreignLanguageId,
            setValue: setForeignLanguageId,
            choices: foreignLanguages,
          },
        ]}
      />
      {filters && (
        <List
          title="Входни тестове"
          sx={settings.style.expandStyle}
          exporter={false}
          resource={`${ActivityServicePath}students/${FOREIGN_LANGUAGES}/${filters.foreignLanguageId}/${LEVELS}/${ASSIGNMENTS}`}
          pagination={false}
          perPage={1000}
          actions={<ListActions foreignLanguageId={filters.foreignLanguageId} />}
        >
          {isMobile ? (
            <SimpleList
              linkType={false}
              sx={settings.style.mobileList}
              primaryText={record => (
                <div>
                  <MobileListRow
                    fields={[{ label: "Факултетен  номер", value: record?.studentFacultyNumber }]}
                  />
                  <MobileListRow
                    fields={[
                      { label: "Име", value: record?.studentFirstName },
                      { label: "Презиме", value: record?.studentSecondName },
                      { label: "Фамилия", value: record?.studentLastName },
                    ]}
                  />
                </div>
              )}
              secondaryText={record => (
                <MobileListRow
                  fields={[
                    {
                      label: "Оценка входящ тест",
                      value: record.entryTestGrade
                        ? `${record.entryTestGradeDescription} (${record.entryTestGradeText})`
                        : "Няма оценка",
                    },
                  ]}
                />
              )}
            />
          ) : (
            <Datagrid
              bulkActionButtons={
                <SecuredComponent permission={permissions.canManageStudentsForeignLanguages}>
                  <ManageStudentsLevelDialog foreignLanguageId={filters.foreignLanguageId} />
                </SecuredComponent>
              }
              rowClick={false}
              sx={settings.style.firstExpandIconDatagrid}
            >
              <TextField
                source="studentFacultyNumber"
                label="Факултетен  номер"
              />
              <TextField
                source="studentFirstName"
                label="Име"
              />
              <TextField
                source="studentSecondName"
                label="Презиме"
              />
              <TextField
                source="studentLastName"
                label="Фамилия"
              />
              <FunctionField
                label="Оценка входящ тест"
                render={record =>
                  record.entryTestGrade
                    ? `${record.entryTestGradeDescription} (${record.entryTestGradeText})`
                    : "Няма оценка"
                }
              />
            </Datagrid>
          )}
        </List>
      )}
    </>
  );
};

export default ListForeignLanguagesStudents;
